import {
  ALL_COUNTRY_CODES,
  COUNTRIES,
  getDefaultCountry as getDefaultCountryCore,
  PHONE_SANDBOX_NUMBERS,
  REGION_CODES,
} from '@onefootprint/core';
import type { Locale } from '@onefootprint/request-types';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/types/src/data';
import {
  AsYouType,
  type CountryCode,
  formatIncompletePhoneNumber,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  parsePhoneNumberWithError,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import type { CountrySelectOption } from '../components/form-country';

const LOCALE_COUNTRY_MAP: Record<Locale, Iso3166TwoDigitCountryCode> = {
  'es-MX': 'MX',
  'en-US': 'US',
  'pt-BR': 'BR',
  'en-AU': 'AU',
  'en-GB': 'GB',
  'en-CA': 'CA',
  'en-IE': 'IE',
  'en-NZ': 'NZ',
  'en-PH': 'PH',
  'en-SG': 'SG',
  'en-IN': 'IN',
  'en-ZA': 'ZA',
  'ja-JP': 'JP',
};

export const sandboxNumbers = PHONE_SANDBOX_NUMBERS;

export const getMask = (country: Iso3166TwoDigitCountryCode) => {
  const example = getExampleNumber(country as CountryCode, examples);
  if (!example) return '(XXX) XXX-XXXX';
  // Replace all digits with 'X'
  return example.formatNational().replace(/\d/g, 'X');
};

// A number that fills the mask is complete only where the country has no longer numbers.
export const isMaskLongestNumber = (country: Iso3166TwoDigitCountryCode) => {
  const example = getExampleNumber(country as CountryCode, examples);
  return !!example && validatePhoneNumberLength(`${example.nationalNumber}0`, country as CountryCode) === 'TOO_LONG';
};

export const getCallingCode = (country: Iso3166TwoDigitCountryCode) => {
  try {
    return `+${getCountryCallingCode(country as CountryCode)}`;
  } catch (_) {
    return REGION_CODES[country];
  }
};

/* We add an extra space to the calling code for better formatting consistency */
export const getCallingCodeWithExtraSpace = (country: Iso3166TwoDigitCountryCode) => {
  return `${getCallingCode(country)} `;
};

export const getCountriesWithCallingCode = (options: CountrySelectOption[] = COUNTRIES) => {
  return options.map(country => {
    const callingCode = getCallingCode(country.value);

    return {
      ...country,
      label: `${country.label} ${callingCode ? `(${callingCode})` : ''}`,
    };
  });
};

export const getDefaultCountry = (locale?: Locale): Iso3166TwoDigitCountryCode => {
  if (locale && LOCALE_COUNTRY_MAP[locale]) {
    return LOCALE_COUNTRY_MAP[locale];
  }

  return getDefaultCountryCore(new Set(ALL_COUNTRY_CODES)).value;
};

export const parseDefaultValue = (defaultValue?: string, locale?: Locale) => {
  const localeCountry = locale ? LOCALE_COUNTRY_MAP[locale] : undefined;
  const fallbackValue = {
    value: getCallingCodeWithExtraSpace(localeCountry ?? 'US'),
    country: localeCountry ?? ('US' as Iso3166TwoDigitCountryCode),
  };

  if (!defaultValue) {
    return fallbackValue;
  }
  try {
    const phoneNumber = parsePhoneNumberWithError(defaultValue);

    if (sandboxNumbers.includes(defaultValue)) {
      return {
        value: `${getCallingCodeWithExtraSpace('US')}${phoneNumber.formatNational()}`,
        country: 'US' as Iso3166TwoDigitCountryCode,
      };
    }

    const country = phoneNumber.country ?? 'US';

    return {
      value: `${getCallingCodeWithExtraSpace(country as Iso3166TwoDigitCountryCode)}${phoneNumber.formatNational()}`,
      country: country as Iso3166TwoDigitCountryCode,
    };
  } catch (_) {
    return fallbackValue;
  }
};

export const toE164 = (value: string, country: Iso3166TwoDigitCountryCode, callingCode: string): string => {
  const instance = new AsYouType(country as CountryCode);
  instance.input(value.startsWith('+') ? value : `${callingCode}${value}`);
  let e164 = instance.getNumber()?.number || '';
  if (validatePhoneNumberLength(e164) === 'TOO_LONG') {
    e164 = e164.slice(0, -1);
  }
  return e164;
};

export const formatToNational = (
  e164Value: string,
  country: Iso3166TwoDigitCountryCode,
  callingCode: string,
): string => {
  return formatIncompletePhoneNumber(e164Value.replace(callingCode, ''), country as CountryCode);
};

export const extractNationalPart = (formattedValue: string, country: Iso3166TwoDigitCountryCode): string => {
  return formattedValue.replace(getCallingCodeWithExtraSpace(country), '');
};

export const resolveInputValue = (
  value: string,
  locale: Locale | undefined,
  callingCode: string,
): { nationalValue: string; country: Iso3166TwoDigitCountryCode } => {
  let parsed = parseDefaultValue(value, locale);
  if (parsed.value.trim() === getCallingCodeWithExtraSpace(parsed.country).trim()) {
    parsed = parseDefaultValue(`${callingCode} ${value}`, locale);
  }
  return {
    nationalValue: extractNationalPart(parsed.value, parsed.country),
    country: parsed.country,
  };
};

export const parsePastedNumber = (text: string, country: Iso3166TwoDigitCountryCode): string => {
  return parsePhoneNumberFromString(text, { defaultCountry: country as CountryCode })?.formatNational() || '';
};

export const isPhoneNumberValid = (value: string, country: Iso3166TwoDigitCountryCode): boolean => {
  if (!value || sandboxNumbers.includes(value)) return true;
  try {
    return isValidPhoneNumber(value, country as CountryCode);
  } catch (_) {
    return false;
  }
};
